import { useState, useEffect, useRef } from 'react';
import { registerPlugin } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { MapPin, Shield, Lock, ExternalLink, X, Check, AlertTriangle } from 'lucide-react';
import { useLocationContext } from '../contexts/LocationContext';

const IS_NATIVE = typeof window !== 'undefined' && window.__IS_NATIVE__ === true;

// Import synchrone du plugin natif (voir useLocationTracking.js pour l'explication
// du bug ".then() is not implemented on ios" causé par le proxy Capacitor).
const BackgroundGeolocation = IS_NATIVE ? registerPlugin('BackgroundGeolocation') : null;

/**
 * Modal "Partage de position" - style Google Maps.
 * S'affiche automatiquement au premier login si GPS non-activé,
 * et est accessible depuis le profil chauffeur.
 */
export const LocationShareModal = ({ open, onClose, forceShow = false }) => {
  const { isTracking, error, startTracking, stopTracking, consent, requestConsent } = useLocationContext();
  const [requesting, setRequesting] = useState(false);
  // Suivi de "l'user vient de cliquer Activer" — évite l'auto-close si le modal
  // s'ouvre alors que le tracking était déjà actif (ex: ouverture depuis le profil).
  const justEnabledRef = useRef(false);

  // DEBUG: Loguer les plugins Capacitor disponibles au montage
  useEffect(() => {
    if (!open) return;
    try {
      const plugins = Object.keys(window.Capacitor?.Plugins || {});
      const hasBG = plugins.includes('BackgroundGeolocation');
      console.log('[DEBUG] IS_NATIVE:', IS_NATIVE);
      console.log('[DEBUG] Capacitor.Plugins:', plugins);
      console.log('[DEBUG] BackgroundGeolocation present:', hasBG);
      console.log('[DEBUG] Capacitor.getPlatform:', window.Capacitor?.getPlatform?.());
    } catch (e) {
      console.error('[DEBUG] error:', e);
    }
  }, [open]);

  // Auto-close UNIQUEMENT après un clic explicite sur "Activer" qui aboutit
  // (isTracking passe à true suite à l'action de l'user). Pas de close si le
  // modal est ouvert alors que le tracking était déjà actif.
  useEffect(() => {
    if (isTracking && justEnabledRef.current && !forceShow) {
      justEnabledRef.current = false;
      const t = setTimeout(() => onClose?.(), 1500);
      return () => clearTimeout(t);
    }
  }, [isTracking, forceShow, onClose]);

  const handleEnable = async () => {
    // Divulgation Google Play : jamais de demande de permission sans « Accepter »
    if (consent !== 'accepted') {
      onClose?.();
      requestConsent?.();
      return;
    }
    setRequesting(true);
    justEnabledRef.current = true; // marque l'intention d'activer explicite

    // Sauvegarder préférence GPS = ON
    try {
      const savedSettings = localStorage.getItem('notificationSettings');
      const settings = savedSettings ? JSON.parse(savedSettings) : {};
      settings.locationTracking = true;
      localStorage.setItem('notificationSettings', JSON.stringify(settings));
    } catch (e) {}

    // Sur iOS natif : vérifier d'abord l'état actuel des permissions.
    // Si déjà refusé, iOS ne re-montrera PAS le popup — il faut envoyer aux Réglages.
    if (IS_NATIVE) {
      try {
        const perms = await Geolocation.checkPermissions();
        console.log('[Modal] Permissions actuelles:', perms);

        // 'denied' = user a refusé définitivement → ouvrir Réglages
        if (perms.location === 'denied' || perms.coarseLocation === 'denied') {
          setRequesting(false);
          await handleOpenSettings();
          return;
        }
      } catch (e) {
        console.warn('[Modal] checkPermissions failed:', e);
      }
    }

    // Lancer le tracking — startTracking() gère lui-même le popup iOS via
    // addWatcher({requestPermissions: true, ...}). PAS DE DOUBLE APPEL.
    try {
      await startTracking();
    } catch (e) {
      console.error('[Modal] startTracking failed:', e);
    } finally {
      // Petit délai pour laisser React propager l'état isTracking / error
      setTimeout(() => setRequesting(false), 500);
    }
  };

  const handleDisable = () => {
    const savedSettings = localStorage.getItem('notificationSettings');
    const settings = savedSettings ? JSON.parse(savedSettings) : {};
    settings.locationTracking = false;
    localStorage.setItem('notificationSettings', JSON.stringify(settings));
    stopTracking();
    onClose?.();
  };

  const handleOpenSettings = async () => {
    if (IS_NATIVE && BackgroundGeolocation) {
      try {
        await BackgroundGeolocation.openSettings?.();
      } catch (e) {
        console.error('[LocationShareModal] Cannot open settings:', e);
      }
    }
  };

  if (!open) return null;

  const isDenied = error && error.toLowerCase().includes('permission');

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 80px)' }}
      data-testid="location-share-modal"
    >
      <div className="bg-slate-900 border border-slate-700 w-full sm:max-w-md rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-300 max-h-[80vh] overflow-y-auto">
        {/* Header avec pattern */}
        <div className="relative bg-gradient-to-br from-blue-600 to-blue-800 p-6 pb-8">
          {!forceShow && (
            <button
              onClick={onClose}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
              data-testid="location-modal-close"
            >
              <X size={16} />
            </button>
          )}
          <div className="w-16 h-16 bg-white/15 rounded-2xl flex items-center justify-center mb-4 mx-auto">
            <MapPin size={32} className="text-white" />
          </div>
          <h2 className="text-xl font-black text-white text-center">
            Partagez votre position
          </h2>
          <p className="text-blue-100 text-sm text-center mt-1">
            Pour un suivi de flotte efficace et conforme
          </p>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          {/* Statut actuel */}
          {isTracking ? (
            <>
              <div className="flex items-center gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-3">
                <div className="w-3 h-3 rounded-full bg-emerald-400 animate-pulse" />
                <div>
                  <div className="text-emerald-300 font-bold text-sm">Position partagée ✓</div>
                  <div className="text-emerald-400/70 text-xs">Votre répartiteur voit votre position en temps réel</div>
                </div>
              </div>

              {/* Rappel : pour suivi continu en veille, permission "Toujours" requise */}
              {IS_NATIVE && (
                <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 text-xs">
                  <div className="flex items-center gap-2 text-blue-300 font-bold mb-1">
                    <Shield size={13} />
                    Pour un suivi continu même téléphone verrouillé
                  </div>
                  <p className="text-blue-100/80 mb-2">
                    Vérifiez que l&apos;option <strong>&laquo; Toujours &raquo;</strong> est activée dans les Réglages iOS (pas seulement &laquo; Lors de l&apos;utilisation &raquo;).
                  </p>
                  <button
                    onClick={handleOpenSettings}
                    className="w-full py-2 bg-blue-600/30 hover:bg-blue-600/50 text-blue-200 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                    data-testid="location-verify-always-btn"
                  >
                    Vérifier dans Réglages iOS <ExternalLink size={12} />
                  </button>
                </div>
              )}
            </>
          ) : isDenied ? (
            <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-3">
              <AlertTriangle size={18} className="text-red-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-red-300 font-bold text-sm">Permission refusée</div>
                <div className="text-red-400/70 text-xs mt-0.5">Vous avez refusé l&apos;accès à la localisation dans les Réglages iOS.</div>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-xl p-3">
              <AlertTriangle size={18} className="text-amber-400 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-amber-300 font-bold text-sm">Erreur GPS</div>
                <div className="text-amber-400/70 text-xs mt-0.5">{error}</div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3 bg-slate-800 rounded-xl p-3">
              <div className="w-3 h-3 rounded-full bg-slate-500" />
              <div className="text-slate-400 text-sm">Position non partagée actuellement</div>
            </div>
          )}

          {/* Bénéfices */}
          <div className="space-y-2">
            <div className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-2">
              Ce que le partage active
            </div>
            <BenefitRow icon={MapPin} text="Adresses auto-remplies dans vos arrets" />
            <BenefitRow icon={Check} text="Coordonnees GPS lors des signatures de livraison" />
            <BenefitRow icon={Shield} text="Conformite Transport Canada (obligation legale)" />
            <BenefitRow icon={Lock} text="Suivi actif meme telephone verrouille" />
          </div>

          {/* Notice confidentialité */}
          <div className="bg-slate-800/50 rounded-lg p-3 text-xs text-slate-400">
            <div className="flex items-center gap-2 text-slate-300 font-semibold mb-1">
              <Shield size={12} />
              Votre vie privee est respectee
            </div>
            <p>Votre position n&apos;est visible que par votre repartiteur pendant votre quart de travail. Vous pouvez desactiver le partage a tout moment ici ou dans les Reglages iOS.</p>
          </div>

          {/* AVERTISSEMENT CRITIQUE swipe-quit */}
          {IS_NATIVE && (
            <div className="bg-amber-500/10 border-l-4 border-amber-500 rounded-r-lg p-3 text-xs">
              <div className="flex items-center gap-2 text-amber-400 font-bold mb-1">
                <AlertTriangle size={13} />
                Multitâche iOS — bon reflexe
              </div>
              <p className="text-amber-100/80 mb-2">
                <strong className="text-emerald-300">✅ OK</strong> — Verrouiller le téléphone, ouvrir Messages, Google Maps, etc. L&apos;app reste vivante en arrière-plan.
              </p>
              <p className="text-amber-100/80">
                <strong className="text-red-300">❌ À ÉVITER</strong> — Ouvrir le multitâche puis <strong>glisser la carte ProPilote vers le haut pour la jeter</strong>. Ça tue l&apos;app et iOS arrêtera le suivi GPS.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="pt-2 space-y-2">
            {!isTracking && !isDenied && (
              <button
                onClick={handleEnable}
                disabled={requesting}
                className="w-full py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-60 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-colors"
                data-testid="location-enable-btn"
              >
                {requesting ? 'Activation...' : 'Activer le partage de position'}
              </button>
            )}

            {isDenied && (
              <button
                onClick={handleOpenSettings}
                className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-colors"
                data-testid="location-open-settings-btn"
              >
                Ouvrir Reglages iOS <ExternalLink size={14} />
              </button>
            )}

            {isTracking && (
              <button
                onClick={handleDisable}
                className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-semibold rounded-xl transition-colors"
                data-testid="location-disable-btn"
              >
                Desactiver le partage
              </button>
            )}

            {!forceShow && !isTracking && (
              <button
                onClick={handleDisable}
                className="w-full py-2 text-slate-500 hover:text-slate-400 text-xs transition-colors"
                data-testid="location-later-btn"
              >
                Plus tard (je perdrai le suivi automatique)
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const BenefitRow = ({ icon: Icon, text }) => (
  <div className="flex items-start gap-2 text-sm text-slate-300">
    <div className="w-6 h-6 rounded-full bg-blue-500/15 flex items-center justify-center shrink-0 mt-0.5">
      <Icon size={12} className="text-blue-400" />
    </div>
    <span>{text}</span>
  </div>
);

/**
 * Hook helper : détecte si le modal doit être auto-affiché
 * au premier login sur une route chauffeur.
 * En natif iOS : toujours proposer si GPS off (comme Google Maps).
 * En web : proposer une seule fois (respecter le choix du user).
 */
export const useLocationShareAutoPrompt = () => {
  const { isTracking, consent } = useLocationContext();
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (isTracking || consent !== 'accepted') return;

    // Sur natif iOS : toujours re-proposer si GPS off (comportement Google Maps).
    // Sur web : respecter le "seen" pour ne pas harceler le user.
    if (!IS_NATIVE) {
      const seen = localStorage.getItem('locationShareModalSeen');
      if (seen === 'true') return;
      const savedSettings = localStorage.getItem('notificationSettings');
      if (savedSettings) {
        try {
          const parsed = JSON.parse(savedSettings);
          if (parsed.locationTracking === false) return;
        } catch (e) {}
      }
    }

    const t = setTimeout(() => setShouldShow(true), 1500);
    return () => clearTimeout(t);
  }, [isTracking, consent]);

  const dismiss = () => {
    localStorage.setItem('locationShareModalSeen', 'true');
    setShouldShow(false);
  };

  return { shouldShow, dismiss };
};

export default LocationShareModal;
